import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@core/store/hooks';
import { selectSessionId } from '@features/chat/store/chatSlice';
import { useGetChatSessionsQuery } from '@features/chat/api/chatApi';

/**
 * Chat history section for the sidebar
 * Displays recent chat sessions with scroll
 */
export function SidebarChatSection() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlSessionId = searchParams.get('session_id');
  const activeSessionId = useAppSelector(selectSessionId);
  const [isExpanded, setIsExpanded] = useState(true);

  // Current active session is either from URL or from Redux state
  const currentSessionId = urlSessionId || activeSessionId;

  // Fetch chat sessions
  const { data: sessions = [], isLoading, isError } = useGetChatSessionsQuery();

  // Don't render section if loading or error
  if (isLoading || isError || sessions.length === 0) {
    return null;
  }

  return (
    <li className="pt-4 hs-overlay-minified:hidden">
      {/* Section Header - Collapsible */}
      <button
        type="button"
        className="w-full px-2.5 text-xs font-semibold uppercase text-muted hover:text-brand flex items-center justify-between group cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>
          {t('sidebar.chats')} ({sessions.length})
        </span>
        <Icon
          icon="mdi:chevron-down"
          className={`size-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Chat Sessions List - Max height with scroll */}
      <div
        className={`w-full overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="max-h-[300px] overflow-y-auto space-y-1 pt-1">
          {sessions.map((session) => {
            const isActive = currentSessionId === session.session_id;
            const baseClasses =
              'min-h-[36px] flex items-center py-2 px-2.5 text-sm rounded-lg hover:bg-brand/5 focus:outline-none focus:bg-brand/5 relative';
            const activeClasses =
              'bg-brand/5 text-brand before:absolute before:-left-3 before:top-0 before:h-full before:w-[3px] before:rounded-r-md';
            const inactiveClasses = 'text-muted';
            const messageText = session.first_message || t('chat.untitled');
            const truncatedText = messageText.length > 28
              ? messageText.substring(0, 28) + '...'
              : messageText;

            return (
              <Link
                key={session.session_id}
                to={`/?session_id=${session.session_id}`}
                className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
              >
                <span className="flex-1">
                  {truncatedText}
                </span>
                {isActive && (
                  <div
                    className="absolute -left-3 top-0 h-full w-[3px] rounded-r-md"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </li>
  );
}
